// B10 slice S20b's screens and console areas (CR-065 "Master Permission Matrix"; CR-066; plan D14), loaded by
// views/b10/register.ts. This folder is `system/` rather than PLAN's `settings/` (PARALLEL-MAP §3 S20b): S20a owns
// `settings/` and its register.ts, and each screen folder registers itself from its own.
//
// Routes (views/b10/shell.ts; a reader whose manifest lists none of a route's gates gets the not-found screen, N02,
// and every read behind them is refused by the server anyway):
//   #settings/system[/<area>]       System Admin — the Admin (DC-228: one Admin holds the console and System Admin):
//                                   gated on the system reads, not on `permissions.matrix` or `audit.query`, so a
//                                   reader of the matrix alone never opens this area.
//   #settings/permissions           the Master Permission Matrix — whoever may read it (the Admin).
//   #admin/integrations[/<is_…>]    System Admin › Integrations, the link B11's recovery tasks carry (C-B11-11), for a
//                                   reader who holds B11's `admin.integrations`.
// The three console areas join S20a's rail in D14's order: Statuses · Reason lists · Taxonomies · Thresholds ·
// Reminders · Templates · Segments · Strategies, at the slots settings/areas.ts keeps for them (40 · 50 · 60).

import { G6_LABELS } from "@/mc";
import { registerConsoleArea } from "../settings/areas";
import { registerView } from "../shell";
import { CONSOLE_AREA_LABELS } from "./labels";
import { PermissionMatrixPage } from "./PermissionMatrixPage";
import { RemindersView } from "./RemindersView";
import { SystemAdmin, SystemAdminIntegrations } from "./SystemAdmin";
import { TemplatesEditor } from "./TemplatesEditor";
import { ThresholdsEditor } from "./ThresholdsEditor";

/** The System Admin area's gate: the reads matrix.yaml gives the Admin alone (system_administration and audit_ledger
 * "Admin"); backend/tests/reporting/test_ta_admin_console_thresholds.py checks each against the registry. */
export const SYSTEM_ADMIN_GATES = ["admin.verify_ledger", "admin.adapters", "admin.secrets"] as const;
export const PERMISSION_MATRIX_GATES = ["permissions.matrix"] as const;
export const INTEGRATIONS_GATES = ["admin.integrations"] as const;

/** Above any broader `settings` pattern S20a registers: these paths are exact. */
const SPECIFIC = 10;

registerView(/^settings\/system(\/[a-z-]+)?$/, SystemAdmin, { lane: "B10", specificity: SPECIFIC, gates: SYSTEM_ADMIN_GATES });
registerView(/^settings\/permissions$/, PermissionMatrixPage, { lane: "B10", specificity: SPECIFIC, gates: PERMISSION_MATRIX_GATES });
registerView(/^admin\/integrations(\/is_[A-Za-z0-9_-]+)?$/, SystemAdminIntegrations, { lane: "B10", specificity: SPECIFIC, gates: INTEGRATIONS_GATES });

/** The console areas this slice adds: key (the `#settings/config/<area>` part), rail position and the kinds whose keys
 * the area lists. The positions are S20a's slots (settings/areas.ts: Statuses 10 · Reason lists 20 · Taxonomies 30 ·
 * Thresholds 40 · Reminders 50 · Templates 60 · Segments 70 · Strategies 80). */
export const S20B_AREAS = [
  { key: "thresholds", label: G6_LABELS.thresholds, order: 40, kinds: ["sla_thresholds", "health_thresholds", "phases"], Editor: ThresholdsEditor },
  { key: "reminders", label: G6_LABELS.reminders, order: 50, kinds: ["metrics", "escalation_policy"], Editor: RemindersView },
  { key: "templates", label: CONSOLE_AREA_LABELS.templates, order: 60, kinds: ["template"], Editor: TemplatesEditor },
] as const;

for (const area of S20B_AREAS) registerConsoleArea(area);
